import { create } from 'zustand';
import { instituteAdminApi } from '@/lib/api';

/**
 * Global admission-session scope for the institute portal.
 *
 * Every institute-scoped list/report endpoint accepts a `session_id` query
 * param (absent = all sessions). The header session selector writes the chosen
 * id here and to localStorage; `lib/api.ts`'s request interceptor reads the
 * same localStorage key and attaches `session_id` to institute GET requests, so
 * pages only have to re-run their fetches when `sessionId` changes.
 *
 * sessionId === '' means "all sessions" (super admin / no allotted sessions).
 */

export const ACTIVE_SESSION_STORAGE_KEY = 'activeSessionId';

export interface SessionOption {
    id: string;
    session_name?: string;
    year?: string | number;
    start_date?: string | null;
    end_date?: string | null;
    extension_end_date?: string | null;
    is_active?: boolean;
    is_current?: boolean;
    /** How many students the institute has in this session (from /sessions). */
    student_count?: number;
    [key: string]: any;
}

interface SessionState {
    sessions: SessionOption[];
    sessionId: string;
    loading: boolean;
    loaded: boolean;
}

interface SessionActions {
    loadSessions: () => Promise<void>;
    setSessionId: (id: string) => void;
    reset: () => void;
}

function readStoredSessionId(): string {
    try {
        return localStorage.getItem(ACTIVE_SESSION_STORAGE_KEY) || '';
    } catch {
        return '';
    }
}

/**
 * Was the stored id CHOSEN by the user, or merely computed as the default?
 *
 * The stored id has to be written either way, because lib/api.ts's request
 * interceptor reads this same localStorage key to attach session_id -- leaving
 * it unset would silently widen every request to all sessions. But a computed
 * default that is persisted becomes indistinguishable from a deliberate choice
 * and then outlives the reason for it for ever: an institute that was once
 * defaulted to the near-empty Summer 2027 stayed pinned there, and no change to
 * how the default is picked could ever reach it. This flag keeps the two apart,
 * so a computed default is recomputed on every load and an explicit pick sticks.
 */
const SESSION_WAS_AUTO_KEY = 'activeSessionIdAuto';

function writeStoredSessionId(id: string, auto = false) {
    try {
        if (id) localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, id);
        else localStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
        if (auto && id) localStorage.setItem(SESSION_WAS_AUTO_KEY, '1');
        else localStorage.removeItem(SESSION_WAS_AUTO_KEY);
    } catch {
        /* storage unavailable — scope stays in-memory only */
    }
}

function storedSessionWasChosen(): boolean {
    try {
        return !!localStorage.getItem(ACTIVE_SESSION_STORAGE_KEY)
            && localStorage.getItem(SESSION_WAS_AUTO_KEY) !== '1';
    } catch {
        return false;
    }
}

function pickDefaultSession(sessions: SessionOption[]): { id: string; auto: boolean } {
    if (!sessions.length) return { id: '', auto: false };

    // 1) Whatever the user picked last, if it is still allotted. Only an
    //    explicit pick counts -- not a default this code wrote earlier.
    const stored = readStoredSessionId();
    if (storedSessionWasChosen() && sessions.some((s) => String(s.id) === stored)) {
        return { id: stored, auto: false };
    }

    // 2) The current active admission session. Several can be current at once
    //    -- Winter runs to 31 December while Summer 2027 has already opened --
    //    so prefer the current session the institute's students are actually
    //    in. Taking the first (newest) one opened the whole portal filtered to
    //    3 students out of 47, which read as missing data rather than a filter.
    const current = sessions.filter((s) => s.is_current);
    const busiest = current
        .slice()
        .sort((a, b) => Number(b.student_count || 0) - Number(a.student_count || 0))[0];
    if (busiest && Number(busiest.student_count || 0) > 0) return { id: String(busiest.id), auto: true };
    if (current.length) return { id: String(current[0].id), auto: true };

    // 3) Newest by start_date (fall back to list order, which is newest first).
    const newest = [...sessions].sort((a, b) => {
        const av = a.start_date ? new Date(a.start_date).getTime() : 0;
        const bv = b.start_date ? new Date(b.start_date).getTime() : 0;
        return bv - av;
    })[0];
    return newest ? { id: String(newest.id), auto: true } : { id: '', auto: false };
}

export const useSessionStore = create<SessionState & SessionActions>()((set, get) => ({
    sessions: [],
    sessionId: readStoredSessionId(),
    loading: false,
    loaded: false,

    loadSessions: async () => {
        if (get().loading) return;
        set({ loading: true });
        try {
            const list = await instituteAdminApi.getTimings();
            const sessions: SessionOption[] = Array.isArray(list) ? list : [];
            if (!sessions.length) {
                // Super admin / user with no institute → no session scoping.
                writeStoredSessionId('');
                set({ sessions: [], sessionId: '', loading: false, loaded: true });
                return;
            }
            const next = pickDefaultSession(sessions);
            writeStoredSessionId(next.id, next.auto);
            set({ sessions, sessionId: next.id, loading: false, loaded: true });
        } catch {
            set({ loading: false, loaded: true });
        }
    },

    setSessionId: (id: string) => {
        const next = id || '';
        writeStoredSessionId(next);
        set({ sessionId: next });
    },

    reset: () => {
        writeStoredSessionId('');
        set({ sessions: [], sessionId: '', loading: false, loaded: false });
    },
}));

/** Currently selected admission session id ('' = all sessions). */
export const useActiveSessionId = (): string => useSessionStore((s) => s.sessionId);

/** Allotted sessions, newest first (as returned by the API). */
export const useSessions = (): SessionOption[] => useSessionStore((s) => s.sessions);

/** Non-reactive read — for use inside callbacks / non-React code. */
export const getActiveSessionId = (): string => useSessionStore.getState().sessionId;
